/**
 * ч.1141: діалог «Скинути перенесення?» — попередження, що дані лишаються, і
 * (для власника акаунта) галочка «прибрати все, що перенесено з 1С» з
 * попереднім переглядом: що буде видалено і що цьому заважає.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { migrationRuns, type MigrationRun } from '@/api/migrationRuns';

/** Що бухгалтер має знати ДО скидання (рішення власника: дані лишаються). */
export const RESET_WARNING =
  'Перенесення повернеться на крок налаштувань: можна змінити модель перенесення '
  + 'і запустити його знову.\n\n'
  + 'Дані, які вже прийнято з 1С, залишаться в сервісі. Повторний імпорт їх '
  + 'оновить — дублів не буде.\n\n'
  + 'Якщо після скидання обрати вужчу модель, уже прийняті документи поза новим '
  + 'періодом залишаться непроведеними.';

export const PURGE_HINT =
  'Усе, що прийнято з 1С у цій організації, буде видалено — документи, договори, '
  + 'банківські рахунки та інше з позначкою «імпортовано». Створене вручну в сервісі '
  + 'лишиться. Якщо на перенесені записи вже посилаються ваші власні — видалення не '
  + 'виконається, і ви побачите, що саме заважає.';

function errorText(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    if (data && typeof data === 'object' && 'detail' in data) {
      const d = (data as { detail: unknown }).detail;
      return typeof d === 'string' ? d : JSON.stringify(d);
    }
    return error.message;
  }
  return String(error);
}

interface Props {
  runId: string;
  onClose: () => void;
  onDone: (run: MigrationRun) => void;
}

export default function MigrationResetDialog({ runId, onClose, onDone }: Props) {
  const [purge, setPurge] = useState(false);
  const preview = useQuery({
    queryKey: ['migration-run-reset-preview', runId],
    queryFn: () => migrationRuns.resetPreview(runId),
  });
  const reset = useMutation({
    mutationFn: () => migrationRuns.reset(runId, { purge_imported: purge }),
    onSuccess: (run) => onDone(run),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const p = preview.data?.purge;
  const canPurge = !!p?.allowed;
  const purgeChecked = purge && canPurge;

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-run-title"
        data-testid="reset-run-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-panel)', borderRadius: 8, padding: '1.25rem 1.5rem',
          minWidth: 320, maxWidth: 560, boxShadow: '0 20px 50px var(--overlay)',
        }}
      >
        <h3 id="reset-run-title" style={{ marginTop: 0 }}>Скинути перенесення?</h3>
        <p style={{ margin: '0 0 1rem', color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>
          {RESET_WARNING}
        </p>

        {preview.isLoading && (
          <p style={{ color: 'var(--muted)', fontSize: '0.9em' }}>Перевіряємо, що прийнято…</p>
        )}
        {preview.isError && (
          <p style={{ color: 'var(--danger)', fontSize: '0.9em' }}>
            Не вдалося перевірити прийняті дані: {errorText(preview.error)}
          </p>
        )}

        {p && (
          <div
            data-testid="reset-purge-block"
            style={{
              padding: '0.75rem', borderRadius: 6, marginBottom: '1rem',
              background: 'var(--bg-soft)', border: '1px solid var(--border)',
            }}
          >
            {canPurge ? (
              <>
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={purgeChecked}
                    onChange={(e) => setPurge(e.target.checked)}
                    data-testid="reset-purge-checkbox"
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span>
                    <strong>прибрати все, що перенесено з 1С</strong>
                    {p.account_level
                      ? ' — разом із довідниками акаунта (організація в акаунті одна)'
                      : ' — документи й довідники цієї організації; спільні довідники акаунта '
                        + '(контрагенти, номенклатура, статті витрат) лишаються, бо в акаунті '
                        + 'кілька організацій'}
                  </span>
                </label>
                <p style={{ margin: '0.5rem 0 0 1.6rem', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                  {PURGE_HINT}
                </p>
                {p.rows > 0 ? (
                  <p data-testid="reset-purge-summary"
                    style={{ margin: '0.5rem 0 0 1.6rem', fontSize: '0.9em' }}>
                    Буде видалено записів: <strong>{p.rows}</strong>
                    {p.highlights.length > 0 && (
                      <> — {p.highlights.map((h) => `${h.label}: ${h.count}`).join(', ')}</>
                    )}
                    {p.files > 0 && <>; файлів вкладень: {p.files}</>}
                  </p>
                ) : (
                  <p style={{ margin: '0.5rem 0 0 1.6rem', fontSize: '0.9em', color: 'var(--muted)' }}>
                    Перенесених з 1С даних у цій організації немає.
                  </p>
                )}
              </>
            ) : (
              <p data-testid="reset-purge-unavailable" style={{ margin: 0, fontSize: '0.9em' }}>
                {p.blockers.length > 0 ? (
                  <>
                    <strong>Прибрати перенесене зараз не можна</strong> — заважає:
                    <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.2rem' }}>
                      {p.blockers.map((b, i) => <li key={i}>{b.text}</li>)}
                    </ul>
                    Скинути перенесення можна і так — дані залишаться.
                  </>
                ) : (
                  <>{p.reason ?? 'Прибрати перенесене недоступно.'}</>
                )}
              </p>
            )}
          </div>
        )}

        {reset.isError && (
          <p data-testid="wizard-reset-error" style={{ color: 'var(--danger)', margin: '0 0 0.75rem' }}>
            {errorText(reset.error)}
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={reset.isPending}>не скидати</button>
          <button
            type="button"
            className="danger"
            onClick={() => reset.mutate()}
            disabled={reset.isPending || preview.isLoading}
            data-testid="reset-run-confirm"
          >
            {reset.isPending
              ? 'скидаємо…'
              : purgeChecked ? 'скинути і прибрати перенесене' : 'так, скинути'}
          </button>
        </div>
      </div>
    </div>
  );
}
